import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Utensils, Phone, MapPin } from "lucide-react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

export default function PublicMenu() {
  const { companyId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (process.env.REACT_APP_MOCK_MODE === "true") {
      try {
        const all = JSON.parse(localStorage.getItem("muhasabaty:mock:products") || "{}");
        const products = all[companyId] || [];
        const groups = new Map();
        products.forEach((p) => {
          const name = p.category || "أصنافنا";
          if (!groups.has(name)) groups.set(name, []);
          groups.get(name).push(p);
        });
        const companyName = companyId === "demo-restaurant" ? "مطعم المذاق التجريبي" : "الشركة التجريبية";
        setData({
          company: { id: companyId, name: companyName, phone: "", address: "" },
          categories: [...groups.entries()].map(([name, items]) => ({ name, items })),
        });
      } catch { setErr("تعذّر تحميل القائمة التجريبية"); }
      return;
    }
    axios.get(`${BACKEND}/api/restaurant/menu/public/${companyId}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "تعذّر تحميل القائمة"));
  }, [companyId]);

  if (err) return <div className="min-h-screen grid place-items-center bg-slate-50 p-6 text-center"><div><Utensils className="w-16 h-16 mx-auto opacity-30 mb-3" /><div className="text-lg font-bold text-red-600">{err}</div></div></div>;
  if (!data) return <div className="min-h-screen grid place-items-center bg-slate-50"><div className="text-muted-foreground">جاري التحميل...</div></div>;

  const { company, categories } = data;

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-orange-50 to-white" data-testid="public-menu">
      {/* Hero */}
      <div className="bg-gradient-to-b from-orange-600 to-orange-500 text-white py-10 px-4 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "20px 20px" }} />
        <div className="relative">
          <div className="w-16 h-16 mx-auto rounded-full bg-white/20 grid place-items-center mb-3 backdrop-blur">
            <Utensils className="w-8 h-8" />
          </div>
          <h1 className="text-3xl font-bold" data-testid="menu-title">{company.name}</h1>
          <div className="text-sm opacity-90 mt-2 space-y-0.5">
            {company.phone && <div className="flex items-center justify-center gap-1"><Phone className="w-3 h-3" /> {company.phone}</div>}
            {company.address && <div className="flex items-center justify-center gap-1"><MapPin className="w-3 h-3" /> {company.address}</div>}
          </div>
        </div>
      </div>

      {/* Menu */}
      <div className="max-w-3xl mx-auto p-4 pb-16">
        {categories.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">القائمة فارغة حالياً</div>
        ) : categories.map(cat => (
          <div key={cat.name} className="mb-8" data-testid={`cat-${cat.name}`}>
            <h2 className="text-xl font-bold text-orange-700 mb-3 pb-2 border-b-2 border-orange-200 flex items-center gap-2">
              <span className="w-2 h-6 bg-orange-500 rounded"></span> {cat.name}
            </h2>
            <div className="space-y-2">
              {cat.items.map(p => (
                <div key={p.id} className="flex gap-3 p-3 bg-white rounded-lg shadow-sm border border-orange-100 hover:shadow-md transition" data-testid={`item-${p.id}`}>
                  {p.image_url && <img src={p.image_url} alt={p.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-800">{p.name}</div>
                    {p.description && <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{p.description}</div>}
                  </div>
                  <div className="text-orange-600 font-bold whitespace-nowrap">
                    {p.sale_price?.toLocaleString()} <span className="text-xs">{p.currency}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="text-center text-xs text-muted-foreground mt-10">
          مدعوم من <span className="font-bold text-orange-600">محاسبتي</span>
        </div>
      </div>
    </div>
  );
}
